/**
 * CESM OSINT System - Alerts Notification Modal
 * Modal affiché au login si des alertes non lues existent
 * 
 * Concept & Développement: CDT (R) Jean-Baptiste GIRAUD
 * LPP Logiciel © 2026
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, AlertTriangle, Zap, X, ArrowRight } from 'lucide-react';

const AlertsNotificationModal = ({ 
  isOpen, 
  onClose, 
  onViewAlerts, 
  unreadCount, 
  criticalCount, 
  highCount,
  recentCritical 
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-marine-navy border-2 border-orange-500/50 rounded-lg w-full max-w-md overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative px-6 py-4 bg-gradient-to-r from-orange-900/50 to-red-900/50 border-b border-orange-500/30">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1 text-white/80 hover:text-white hover:bg-white/10 rounded"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-900/50 rounded relative">
                <Bell className="w-8 h-8 text-orange-400" />
                <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              </div>
              <div>
                <h2 className="font-heading text-orange-400 text-xl tracking-wider">
                  ALERTES NON LUES
                </h2>
                <p className="font-mono text-white/80 text-sm">
                  Activité détectée sur vos mots-clés
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="font-mono text-white text-lg mb-4 text-center">
              Vous avez <span className="text-orange-400 font-bold">{unreadCount}</span> alerte{unreadCount > 1 ? 's' : ''} non lue{unreadCount > 1 ? 's' : ''}
            </p>

            {/* Priority breakdown */}
            <div className="flex justify-center gap-4 mb-6">
              {criticalCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-900/30 border border-red-500/50 rounded">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="font-mono text-red-400">
                    {criticalCount} CRITIQUE{criticalCount > 1 ? 'S' : ''}
                  </span>
                </div>
              )}
              {highCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-orange-900/30 border border-orange-500/50 rounded">
                  <Zap className="w-5 h-5 text-orange-400" />
                  <span className="font-mono text-orange-400">
                    {highCount} HAUTE{highCount > 1 ? 'S' : ''}
                  </span>
                </div>
              )}
            </div>

            {/* Recent critical alert preview */}
            {recentCritical && (
              <div className="mb-6 p-3 bg-red-900/20 border border-red-500/30 rounded">
                <p className="font-mono text-xs text-red-400 mb-1">Alerte critique récente:</p>
                <p className="font-mono text-sm text-white line-clamp-2">
                  {recentCritical.title}
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {recentCritical.keywords_matched?.slice(0, 3).map((kw, i) => (
                    <span key={i} className="px-2 py-0.5 bg-red-900/40 text-red-300 font-mono text-xs rounded">
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-marine-slate/30 border border-marine-slate/50 text-white/90 font-mono text-sm hover:bg-marine-slate/50 rounded"
              >
                Plus tard
              </button>
              <button
                onClick={onViewAlerts}
                className="flex-1 px-4 py-3 bg-orange-900/50 border border-orange-500 text-orange-400 font-mono text-sm hover:bg-orange-900/70 rounded flex items-center justify-center gap-2"
                data-testid="view-alerts-btn"
              >
                Consulter
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AlertsNotificationModal;
